import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  profile: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Demo user for fallback when Supabase is not available
const DEMO_USER = {
  id: 'demo-user-id',
  email: 'admin@travistechrepair.com',
  role: 'admin'
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      setProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for demo token first
        const demoToken = localStorage.getItem('authToken');
        if (demoToken === 'demo-token') {
          // Set demo user
          setUser(DEMO_USER as any);
          setProfile({
            id: 'demo-profile-id',
            user_id: DEMO_USER.id,
            role: 'admin',
            email: DEMO_USER.email
          });
          setLoading(false);
          return;
        }

        // Check if we're on an admin route
        const isAdminRoute = window.location.pathname.startsWith('/admin') || 
                           window.location.pathname === '/admin-login';

        if (isAdminRoute) {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Session error:', error);
            // Check if this is a network or configuration error
            if (error.message.includes('Failed to fetch') || 
                error.message.includes('NetworkError') ||
                error.message.includes('Supabase not configured')) {
              // Fall back to demo mode
              setAuthError('Authentication service is unavailable. Using demo mode.');
              // Set demo user
              setUser(DEMO_USER as any);
              setProfile({
                id: 'demo-profile-id',
                user_id: DEMO_USER.id,
                role: 'admin',
                email: DEMO_USER.email
              });
            } else {
              setAuthError('Failed to get session: ' + error.message);
            }
          } else if (session?.user) {
            setUser(session.user);
            await fetchProfile(session.user.id);
          }
        }
        
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
          if (event === 'SIGNED_IN' && session?.user) {
            setUser(session.user);
            await fetchProfile(session.user.id);
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setProfile(null);
            // Clear demo token if it exists
            localStorage.removeItem('authToken');
          }
        });

        setLoading(false);

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthError('Failed to initialize authentication');
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      // Check for demo credentials
      if (email === 'admin@travistechrepair.com' && password === 'admin123') {
        // Set demo token
        localStorage.setItem('authToken', 'demo-token');
        // Set demo user
        setUser(DEMO_USER as any);
        setProfile({
          id: 'demo-profile-id',
          user_id: DEMO_USER.id,
          role: 'admin',
          email: DEMO_USER.email
        });
        toast.success('Signed in successfully with demo credentials');
        return;
      }
      
      const { error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password: password.trim()
      });

      if (error) {
        if (error.message === 'Failed to fetch') {
          throw new Error('Network error. Please check your connection and try again.');
        } else if (error.message.includes('Supabase not configured')) {
          throw new Error('Authentication service is not available. Please try again later or contact support.');
        } else {
          throw error;
        }
      }
      
      toast.success('Signed in successfully');
    } catch (error: any) {
      console.error('Sign in error:', error);
      
      let errorMessage = 'Failed to sign in';
      if (error.message) {
        errorMessage = error.message === 'Invalid login credentials'
          ? 'Invalid email or password'
          : error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password: password.trim(),
        options: {
          data: {
            role: email.endsWith('@travistechrepair.com') ? 'technician' : 'customer'
          }
        }
      });

      if (error) throw error;
      
      toast.success('Account created successfully! Please check your email to verify your account.');
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast.error(error.message || 'Failed to create account');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      // Check for demo token
      if (localStorage.getItem('authToken') === 'demo-token') {
        localStorage.removeItem('authToken');
        setUser(null);
        setProfile(null);
        toast.success('Signed out successfully');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setProfile(null);
      sessionStorage.clear();
      localStorage.clear();
      
      toast.success('Signed out successfully');
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast.error(error.message || 'Failed to sign out');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.toLowerCase().trim(),
        {
          redirectTo: `${window.location.origin}/password-reset`,
        }
      );

      if (error) throw error;
      
      toast.success('Password reset instructions sent to your email');
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(error.message || 'Failed to send reset instructions');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {authError ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-orange-600 mb-4">Authentication Notice</h2>
            <p className="mb-6">{authError}</p>
            <p className="text-gray-600 mb-4">
              Continuing in demo mode. You can use the application with limited functionality.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
            >
              Retry Connection
            </button>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}